<template>
  <StandardEmptyPageContainer>
    <v-row class="justify-center pt-5">
      <v-col cols="12" lg="8">
        <v-row class="text-left">
          <v-col>
            <h1>Let's start with some basic information about your company</h1>
          </v-col>
        </v-row>
        <StandardCardContainer radius="small" class="mt-4">
          <template #title> Company Information </template>
          <v-row class="pt-6">
            <v-col>
              <v-text-field
                v-model="company.companyName"
                label="Company Name"
                :error="v$.companyName.$error"
                :error-messages="v$.companyName.$errors[0]?.$message"
                variant="outlined"
                density="compact"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="company.organisationNumber"
                label="Business Registration Number"
                :error="v$.organisationNumber.$error"
                :error-messages="v$.organisationNumber.$errors[0]?.$message"
                variant="outlined"
                density="compact"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <PAutocomplete
                :items="
                  itemsStore.countries.map((c) => ({
                    id: c.code,
                    text: c.name,
                  }))
                "
                :select-text="company.operatingCountryCode"
                placeholder="Country"
                label="Country"
                :error="v$.operatingCountryCode.$error"
                :error-messages="v$.operatingCountryCode.$errors[0]?.$message"
                @field-updated="updateCountry"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-divider class="mb-6" />
              <v-row>
                <v-col class="text-left">
                  <PActionButton
                    class="me-auto"
                    appkit-icon="icon-left-chevron-fill"
                    :icon-size="'xs'"
                    :prepend-icon="true"
                    :variant="'outlined'"
                    @click="goToWelcomeScreen"
                    >Back</PActionButton
                  >
                </v-col>
                <v-col class="text-right">
                  <PActionButton
                    :append-icon="true"
                    :icon-size="'xs'"
                    :loading="isLoading"
                    appkit-icon="icon-right-chevron-fill"
                    @click="post"
                    >Continue
                  </PActionButton>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </StandardCardContainer>
      </v-col>
    </v-row>
  </StandardEmptyPageContainer>
</template>

<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { computed, ref } from "vue";
import { useItemsStore } from "@/stores/itemsStore";
import { Company } from "@/models/company";
import { registerDefaultCompany } from "@/services/companyService";
import { RouterPath } from "@/models/enum/routerPath";
import { useAuthenticationStore } from "@/stores/authentication";
import { useRouter } from "vue-router";
import { useUserProfileStore } from "@/stores/userProfileStore";
import { useSnackBarStore } from "@/stores/snackBarStore";
import { useTrustGroupStore } from "@/stores/trustGroup";
import StandardEmptyPageContainer from "@/components/containers/StandardEmptyPageContainer.vue";
import PActionButton from "@/components/custom/buttons/PActionButton.vue";
import StandardCardContainer from "@/components/containers/StandardCardContainer.vue";
import PAutocomplete from "@/components/custom/PAutocomplete.vue";

const router = useRouter();
const authenticationStore = useAuthenticationStore();
const userProfileStore = useUserProfileStore();
const trustGroupStore = useTrustGroupStore();
const snackBarStore = useSnackBarStore();
const itemsStore = useItemsStore();
const company = ref(new Company());
const isLoading = ref(false);

const rules = computed(() => ({
  companyName: { required },
  organisationNumber: { required },
  operatingCountryCode: { required },
}));

const v$ = useVuelidate<Company>(rules, company);

async function post(): Promise<void> {
  const isValid = await v$.value.$validate();
  if (!isValid) {
    return;
  }

  try {
    isLoading.value = true;
    await registerDefaultCompany(company.value);
    await Promise.all([
      authenticationStore.getAuthenticationStatus(),
      userProfileStore.setUserProfile(),
      trustGroupStore.setIsCustomer(),
      userProfileStore.setTrustGroup(),
    ]);
    router.push(RouterPath.AddColleaguesView);
  } catch (e) {
    snackBarStore.showError();
  } finally {
    isLoading.value = false;
  }
}

async function goToWelcomeScreen(): Promise<void> {
  router.push(RouterPath.RegisterSupplierWelcome);
}

function updateCountry(val: string) {
  company.value.operatingCountryCode = val;
}
</script>
